import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

// ---------------------------------------------------------------------------
// Queue + detail (existing)
// ---------------------------------------------------------------------------

export interface CaseRow {
  caseId: string
  loanAccountNo: string
  customerName: string
  productType: string
  currentDpd: number | null
  currentBucket: string | null
  /** Branch the loan is booked at; null when unmapped. */
  branchCode: string | null
  currentOutstanding: string | null
  /** The demand — overdue + charges. currentOutstanding above is POS. Null
   *  when the lender's file gave no principal/interest split. */
  currentOverdue?: string | null
  priorityBand: string | null
  priorityScore: number
  asOfDate: string | null
  openedAt: string | null
  /** Customer DNC flag — surfaced inline on each row as a red pill so the
   *  agent sees it before opening the case detail. Defaults to false for
   *  older backend responses that didn't carry the field. */
  dncFlag: boolean
  /** The collection agent — who holds the ACTIVE FIELD allocation. */
  fieldAgentUserId?: string | null
  fieldAgentEmpId?: string | null
  fieldAgentName?: string | null
}

export interface Head {
  caseId: string
  loanAccountNo: string
  customerName: string
  externalCustomerRef: string
  productType: string
  sanctionAmount: string | null
  disbursalDate: string | null
  emiAmount: string | null
  /** Loan tenure in months as ingested from LOAN_MASTER; null when absent. */
  tenureMonths: number | null
  /** ROI as ingested from LOAN_MASTER; null when absent. */
  interestRate: string | null
  /** customers.current_address JSONB as a raw JSON string. */
  currentAddressJson: string | null
  /** Decrypted primary mobile (E.164); disclosure is PII-audited server-side. */
  mobile: string | null
  /** The customer's father, from the customer master. Absent from older APIs. */
  fatherName?: string | null
  /** His mobile, masked; the digits never reach the browser. */
  fatherMobileMasked?: string | null
  status: string
  openReason: string | null
  closeReason: string | null
  currentDpd: number | null
  currentBucket: string | null
  /** POS — principal left on the loan. Portfolio data, not the demand. */
  currentOutstanding: string | null
  /** What we ask the borrower to pay. Null until a post-V31 DPD file lands. */
  currentOverdue: string | null
  priorityBand: string | null
  priorityScore: number
  asOfDate: string | null
  openedAt: string | null
  closedAt: string | null
}

export interface DpdTick {
  asOfDate: string
  dpd: number
  bucket: string
  outstandingPrincipal: string | null
  totalOverdue: string | null
  lastPaymentDate: string | null
}

/**
 * Component breakup of current dues, from the newest DPD snapshot.
 * Null when the loan has never appeared in a DPD feed.
 *
 * `asOfDate` is not decoration — these are snapshot values, so any surface
 * rendering the amounts must render the date beside them.
 */
/**
 * What the borrower is asked to pay, and what it is made of.
 *
 * Every line is an OVERDUE amount, so the six components sum to `totalDue`.
 * The previous shape led with POS — principal left on the whole loan — and
 * ended with the arrears total, stacked as though one added up to the other;
 * they never did. POS is portfolio data and now lives on `Head.currentOutstanding`.
 */
export interface DuesBreakup {
  asOfDate: string
  principalDue: string
  interestDue: string
  bounceCharges: string
  latePaymentCharges: string
  otherPenalties: string
  otherFees: string
  /** The demand — the six lines above, summed. */
  totalDue: string
  /**
   * CONFIRMED collections taken after `asOfDate`; "0" when none.
   *
   * The server never rewrites `totalDue` when money is collected — that figure
   * belongs to the lender's LMS and a collections platform that decrements its
   * own copy starts disagreeing with the LMS. This is what shows the
   * collection landed.
   */
  collectedSince: string
  /** `totalDue` less `collectedSince`. */
  netExpected: string
}

export interface CaseDetail {
  head: Head
  dpdTimeline: DpdTick[]
  dues: DuesBreakup | null
}

export type PtpFilter = 'DUE_TODAY' | 'DUE_3D' | 'DUE_SOON' | 'OVERDUE' | 'OPEN'

export type PriorityBand = 'P1' | 'P2' | 'P3' | 'P4'

/** PAID — anything collected since the dues file; UNPAID — nothing yet. */
export type CollectionFilter = 'PAID' | 'UNPAID'

/** Server-side order. "due" is the demand, never POS; every sort pages stably. */
export type CaseSort = 'priority' | 'due' | 'dpd' | 'ptp'

/** bucket / ptp / priority take several values: any of them matches (OR),
 *  while different filters still narrow each other (AND). */
export interface CasesQuery {
  mine?: boolean
  bucket?: string | string[]
  /** Branch node code, e.g. BR-KNP. Narrows the pool to one branch. */
  branch?: string
  /** Exact product_type as ingested. Options come from useCaseProducts(). */
  product?: string
  slot?: 'TELE' | 'FIELD'
  ptp?: PtpFilter | PtpFilter[]
  /** true → only cases with an ACTIVE allocation; false → only unallocated. */
  allocated?: boolean
  priority?: PriorityBand | PriorityBand[]
  /** Paid or unpaid this cycle — the dashboard's split, same server rule. */
  collection?: CollectionFilter
  sort?: CaseSort
  /** Only cases actively allocated to this agent (user id). */
  agent?: string
  /** Server-side search over every case in view: loan no, customer name,
   *  product, customer ref (substring), or a whole mobile number. */
  q?: string
  limit?: number
  offset?: number
}

/**
 * Distinct products across the open cases the caller can see. product_type is
 * free text out of LOAN_MASTER, so the filter's options have to come from the
 * data rather than a hard-coded list.
 */
export function useCaseProducts(query: Pick<CasesQuery, 'allocated' | 'slot' | 'mine'> = {}) {
  // Must mirror the list query. A manager reaches the pool by branch but their
  // queue by allocation, so options fetched without `allocated` come back empty
  // for a manager who has not allocated anything yet.
  const params: Record<string, string | number | boolean> = {}
  if (query.allocated !== undefined) params.allocated = query.allocated
  if (query.slot) params.slot = query.slot
  if (query.mine) params.mine = true
  return useQuery({
    queryKey: ['cases', 'products', params],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get<string[]>('/v1/cases/products', { params })
      return res.data
    },
  })
}

/** A list-valued filter as the API takes it: comma-separated; empty when none. */
export function joinFilter(value: string | readonly string[] | undefined): string {
  if (value === undefined) return ''
  return typeof value === 'string' ? value : value.join(',')
}

export function casesParams(query: CasesQuery): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { limit: query.limit ?? 100 }
  if (query.mine) params.mine = true
  const bucket = joinFilter(query.bucket)
  if (bucket) params.bucket = bucket
  if (query.branch) params.branch = query.branch
  if (query.product) params.product = query.product
  if (query.slot) params.slot = query.slot
  const ptp = joinFilter(query.ptp)
  if (ptp) params.ptp = ptp
  if (query.allocated !== undefined) params.allocated = query.allocated
  const priority = joinFilter(query.priority)
  if (priority) params.priority = priority
  if (query.collection) params.collection = query.collection
  if (query.sort && query.sort !== 'priority') params.sort = query.sort
  if (query.offset) params.offset = query.offset
  if (query.agent) params.agent = query.agent
  const q = query.q?.trim()
  if (q) params.q = q
  return params
}

/**
 * @param opts.keepPrevious keep the last page on screen while the next one
 *   loads, so paging and typing in the search box don't flash the skeleton.
 */
export function useCases(
  query: CasesQuery = {},
  opts: { refetchInterval?: number; keepPrevious?: boolean } = {},
) {
  const params = casesParams(query)
  return useQuery({
    queryKey: ['cases', 'list', params],
    queryFn: async () => {
      const res = await api.get<CaseRow[]>('/v1/cases', { params })
      return res.data
    },
    refetchInterval: opts.refetchInterval ?? 5000,
    ...(opts.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  })
}

export interface CaseTotals {
  /** Matching cases across every page. */
  total: number
  /** POS summed over those cases, as on the dashboard. */
  outstanding: string
  p1Count: number
  p2Count: number
}

/**
 * Totals over every case the list would page through for the same filters
 * and search — the queue's KPI cards and its "of N". Adding up the fetched
 * page in the browser described page 1, not the queue.
 */
export function useCaseTotals(query: Omit<CasesQuery, 'limit' | 'offset' | 'sort'> = {}) {
  const params = casesParams(query)
  delete params.limit
  return useQuery({
    queryKey: ['cases', 'totals', params],
    queryFn: async () => {
      const res = await api.get<CaseTotals>('/v1/cases/totals', { params })
      return res.data
    },
    placeholderData: keepPreviousData,
    refetchInterval: 5000,
  })
}

// ---------------------------------------------------------------------------
// Dashboard / PTP stats
// ---------------------------------------------------------------------------

export interface PtpStats {
  open: number
  dueToday: number
  dueSoon: number
  dueIn3d: number
  overdue: number
  keptIn30d: number
  brokenIn30d: number
  partialIn30d: number
  keptViaLateIn30d: number
  /** kept / (kept + broken + partial) over the last 30d; null when no resolutions. */
  keepRate30d: string | null
  asOf: string
}

export function usePtpStats() {
  return useQuery({
    queryKey: ['dashboard', 'ptp'],
    queryFn: async () => {
      const res = await api.get<PtpStats>('/v1/dashboard/ptp')
      return res.data
    },
    refetchInterval: 30_000,
  })
}

// ---------------------------------------------------------------------------
// Agent productivity, recon aging, unallocated
// ---------------------------------------------------------------------------

export interface AgentProductivityRow {
  userId: string
  empId: string
  fullName: string | null
  dispositions: number
  connects: number
  promises: number
  ptpsKept30d: number
  ptpsResolved30d: number
  /** 0.0 – 1.0; null when dispositions=0 */
  connectRate: string | null
  /** 0.0 – 1.0; null when connects=0 */
  ptpRate: string | null
  /** 0.0 – 1.0; null when ptpsResolved30d=0 */
  keepRate: string | null
}

export function useAgentProductivity(windowDays = 7, limit = 10) {
  return useQuery({
    queryKey: ['dashboard', 'agents', windowDays, limit],
    queryFn: async () => {
      const res = await api.get<AgentProductivityRow[]>('/v1/dashboard/agents', {
        params: { windowDays, limit },
      })
      return res.data
    },
    refetchInterval: 300_000,
  })
}

export interface ReconAging {
  openTotal: number
  openToday: number
  open1to3d: number
  open4to7d: number
  openOver7d: number
  investigating: number
  byKind: { kind: string; count: number }[]
  oldestOpen: string | null
}

export function useReconAging() {
  return useQuery({
    queryKey: ['dashboard', 'recon-aging'],
    queryFn: async () => {
      const res = await api.get<ReconAging>('/v1/dashboard/recon-aging')
      return res.data
    },
    refetchInterval: 60_000,
  })
}

export interface Unallocated {
  total: number
  byBucket: { bucket: string; count: number; outstanding: string }[]
}

export function useUnallocated() {
  return useQuery({
    queryKey: ['dashboard', 'unallocated'],
    queryFn: async () => {
      const res = await api.get<Unallocated>('/v1/dashboard/unallocated')
      return res.data
    },
    refetchInterval: 60_000,
  })
}

export function useCase(caseId: string | null | undefined) {
  return useQuery({
    queryKey: ['cases', caseId],
    enabled: !!caseId,
    queryFn: async () => {
      const res = await api.get<CaseDetail>(`/v1/cases/${caseId}`)
      return res.data
    },
  })
}

// ---------------------------------------------------------------------------
// Action verbs: disposition, PTP, activity
// ---------------------------------------------------------------------------

export type ContactResult =
  | 'CONNECTED'
  | 'NO_ANSWER'
  | 'BUSY'
  | 'SWITCHED_OFF'
  | 'INVALID_NUMBER'
  | 'WRONG_NUMBER'
  | 'CALLBACK_REQUESTED'
  /** CDC — picked up, then cut before anything was said. */
  | 'CUSTOMER_DISCONNECTED'
  | 'PERMANENT_NON_CONTACTABLE'

export type Outcome =
  | 'PROMISE_TO_PAY'
  | 'PARTIAL_PROMISE'
  | 'ALREADY_PAID'
  | 'REFUSAL'
  | 'DISPUTE'
  | 'NEGOTIATION'
  | 'NOT_REACHABLE'
  | 'THIRD_PARTY_RESPONSE'
  | 'NO_RESPONSE_NEEDED'
  | 'DECEASED_REPORTED'
  | 'CLOSURE_REQUEST'
  | 'SETTLEMENT_REQUEST'
  | 'BROKEN_PTP'

/** Outcomes the branch acts on — the server refuses them without a note. */
export const NOTE_REQUIRED_OUTCOMES: ReadonlySet<string> =
  new Set(['DECEASED_REPORTED', 'CLOSURE_REQUEST', 'SETTLEMENT_REQUEST'])

export type Channel = 'TELE' | 'FIELD' | 'MANUAL'

export interface PtpInline {
  promisedAmount: string
  promisedDate: string // YYYY-MM-DD
  graceDays?: number | undefined
  notes?: string | undefined
}

export interface DispositionRequest {
  /** Omit it. The server derives the channel from the caller's own ACTIVE
   *  allocation slot on the case — a client-asserted channel is what made
   *  every per-channel metric untrustworthy. Send it ONLY to break a genuine
   *  tie, when the signed-in user holds both the TELE and the FIELD
   *  allocation on this case; anything else is ignored, and an unheld slot is
   *  refused with `channel_ambiguous`. */
  channel?: Channel
  contactResult: ContactResult
  outcome?: Outcome | null
  tenantDispositionCode?: string | null
  notes?: string | null
  occurredAt?: string | null
  ptp?: PtpInline | null
  /** Conduct overrides to apply. Required when conduct-status reports any
   *  active blocks; each override needs the matching block code + reason
   *  + the user must hold the required permission. */
  overrides?: ConductOverrideInput[] | null
  /** What the calling console knows about the call. Absent for a hand-typed
   *  capture and for the mobile app. */
  call?: DispositionCallInput | null
}

/**
 * `dialledNumberRef` is a LABEL (MOBILE_PRIMARY / REFERENCE_2 …), never a
 * number — the browser is never given one to send.
 */
export interface DispositionCallInput {
  dialledNumberRef?: string | null
  /** Required when contactResult is CALLBACK_REQUESTED. */
  callbackAt?: string | null
  mood?: 'NORMAL' | 'ANGER' | 'IRATE' | null
  preferredLanguage?: string | null
  providerCallId?: string | null
  callStartedAt?: string | null
  callEndedAt?: string | null
  recordingUrl?: string | null
}

export interface DispositionResponse {
  dispositionId: string
  ptpId: string | null
}

export type ActivityKind =
  | 'DISPOSITION'
  | 'PTP'
  | 'ALLOCATION'
  | 'CASE_STATUS'
  | 'RECEIPT'
  | 'PAYMENT'

/** What the dialer knew about the call. All fields null for a hand-typed
 *  capture — which is how the console tells a dialled call from a typed one. */
export interface ActivityCall {
  dialledNumberRef: string | null
  callbackAt: string | null
  callbackStatus: 'SCHEDULED' | 'DONE' | 'MISSED' | null
  mood: 'NORMAL' | 'ANGER' | 'IRATE' | null
  providerCallId: string | null
  callStartedAt: string | null
  callEndedAt: string | null
  recordingUrl: string | null
}

export interface ActivityItem {
  kind: ActivityKind
  id: string
  at: string
  actorEmpId: string | null
  channel: string | null
  /** Present on DISPOSITION rows only. */
  call?: ActivityCall | null
  disposition: {
    contactResult: string
    outcome: string | null
    tenantDispositionCode: string | null
    notes: string | null
    linkedPtpId: string | null
  } | null
  ptp: {
    promisedAmount: string
    promisedDate: string
    graceDays: number
    status: string
    notes: string | null
    resolvedAt: string | null
    resolvedReason: string | null
    keptViaLateData: boolean
    /** Where the field app took the promise; null for tele/web PTPs and
     *  older servers. BigDecimal on the wire: a number, tolerated as a string. */
    geoLat?: number | string | null
    geoLng?: number | string | null
    geoAccuracyM?: number | string | null
    mockLocationDetected?: boolean
  } | null
  /** ASSIGNED = granted to an agent; ENDED = transferred or revoked. */
  allocation: {
    event: 'ASSIGNED' | 'ENDED'
    slot: 'FIELD' | 'TELE'
    agentEmpId: string | null
    agentName: string | null
    status: string
    endReason: string | null
  } | null
  caseStatus: {
    eventType: string
    dpd: number | null
    bucket: string | null
    outstanding: string | null
    /** Non-null when an ingest batch drove the change. */
    sourceBatchId: string | null
  } | null
  /**
   * Money against the case, for both RECEIPT (field cash) and PAYMENT
   * (gateway). `status` replaced `cancelledAt`: a gateway collection can be
   * FAILED or EXPIRED, which a nullable timestamp cannot express.
   */
  receipt: {
    amount: string
    paymentMode: string
    /** CONFIRMED | PENDING | DISCREPANCY | FAILED | EXPIRED | CANCELLED | CREATED */
    status: string | null
  } | null
}

export function useActivity(caseId: string | null | undefined) {
  return useQuery({
    queryKey: ['cases', caseId, 'activity'],
    enabled: !!caseId,
    queryFn: async () => {
      const res = await api.get<ActivityItem[]>(`/v1/cases/${caseId}/activity`, {
        params: { limit: 50 },
      })
      return res.data
    },
    refetchInterval: 5000,
  })
}

export function useCaptureDisposition(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: DispositionRequest) => {
      const res = await api.post<DispositionResponse>(`/v1/cases/${caseId}/dispositions`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases', caseId, 'activity'] })
      qc.invalidateQueries({ queryKey: ['cases', caseId] })
    },
  })
}

export interface PtpStandaloneRequest {
  /** Omit it — derived server-side exactly as on DispositionRequest. */
  channel?: Channel
  promisedAmount: string
  promisedDate: string
  graceDays?: number
  notes?: string | null
}

// ---------------------------------------------------------------------------
// Conduct guardrails — DNC, contact hours, frequency cap
// ---------------------------------------------------------------------------

export interface ConductBlock {
  code: 'dnc_blocked' | 'contact_hours_blocked' | 'contact_frequency_exceeded' | 'deceased_reported'
  message: string
  overridable: boolean
  requiredPermission: string
}

export interface ConductStatus {
  customerId: string | null
  loanAccountNo: string | null
  dnc: boolean
  withinContactHours: boolean
  contactCountToday: number
  frequencyCap: number
  contactHoursStart: string
  contactHoursEnd: string
  contactZone: string
  blocks: ConductBlock[]
  enforced: boolean
  /** When a death was reported for this customer; contact stays blocked until cleared. */
  deceasedReportedAt?: string | null
}

/** A manager clears a reported death so contact can resume. */
export function useClearDeceasedReport(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (reason: string) => {
      await api.post(`/v1/cases/${caseId}/deceased-report/clear`, { reason })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases', caseId, 'conduct-status'] })
      qc.invalidateQueries({ queryKey: ['cases', caseId, 'activity'] })
    },
  })
}

/**
 * Pre-flight read so the UI knows what to render before the agent tries to
 * capture. Refreshes every 60s because contact-hours boundaries actually
 * move (e.g. 19:30 IST → out of window at 19:31 even with the same data).
 */
export function useConductStatus(caseId: string | null | undefined) {
  return useQuery({
    queryKey: ['cases', caseId, 'conduct-status'],
    enabled: !!caseId,
    queryFn: async () => {
      const res = await api.get<ConductStatus>(`/v1/cases/${caseId}/conduct-status`)
      return res.data
    },
    refetchInterval: 60_000,
  })
}

export interface ConductOverrideInput {
  code: ConductBlock['code']
  reason: string
}

export function useOpenPtp(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: PtpStandaloneRequest) => {
      const res = await api.post<{ ptpId: string }>(`/v1/cases/${caseId}/ptps`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases', caseId, 'activity'] })
    },
  })
}
